import { TaskBoardTask, TaskProject } from '../types';
import { isTaskDone, ColumnAggregationMode } from './projectConstants';
import { isAssigneeForUser, SystemUser } from './projectAssignees';

export interface ProjectFilters {
  search: string;
  assignee: string;
  priority: string;
  tag: string;
  dateFrom: string;
  dateTo: string;
  myTasks: boolean;
  hideCompleted: boolean;
  currentUserEmail?: string | null;
  currentUserName?: string | null;
  systemUsers?: SystemUser[];
}

export const defaultFilters = (): ProjectFilters => ({
  search: '',
  assignee: '',
  priority: '',
  tag: '',
  dateFrom: '',
  dateTo: '',
  myTasks: false,
  hideCompleted: false,
});

export function sortTaskProjects(projects: TaskProject[]): TaskProject[] {
  return [...projects].sort((a, b) => {
    const orderDiff = (a.sort_order ?? 0) - (b.sort_order ?? 0);
    if (orderDiff !== 0) return orderDiff;
    return a.name.localeCompare(b.name, 'es');
  });
}

export function getProjectColumns(project: TaskProject | null): string[] {
  if (project?.status_columns && project.status_columns.length > 0) {
    return project.status_columns;
  }
  return ['To-do', 'En proceso', 'En revisión', 'Bloqueado', 'Hecho'];
}

/** Convierte hex (#RRGGBB) a rgba con alpha */
export function hexToRgba(hex: string, alpha: number): string {
  const h = hex.replace('#', '');
  if (h.length !== 6) return hex;
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

/** Fondo atenuado del color del proyecto (misma lógica que columnas kanban) */
export function getProjectMutedBg(
  project: TaskProject | null,
  alpha = 0.18
): string | undefined {
  const hex = project?.color;
  if (!hex) return undefined;
  return hexToRgba(hex, alpha);
}

export function getColumnAggregations(project: TaskProject | null): Record<string, ColumnAggregationMode> {
  if (!project?.column_aggregations || typeof project.column_aggregations !== 'object') return {};
  return project.column_aggregations;
}

export function formatAggregationNumber(value: number): string {
  if (Number.isInteger(value)) return value.toLocaleString('es-AR');
  return value.toLocaleString('es-AR', { maximumFractionDigits: 2, minimumFractionDigits: 0 });
}

/** Parsea un token numérico: "," decimal y "." separador de miles */
export function parseLocalizedNumber(raw: string): number | null {
  const value = raw.trim();
  if (!value || !/^[\d.,]+$/.test(value)) return null;

  const hasComma = value.includes(',');

  if (hasComma) {
    const normalized = value.replace(/\./g, '').replace(',', '.');
    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : null;
  }

  if (value.includes('.')) {
    const parts = value.split('.');
    const lastPart = parts[parts.length - 1];

    const isThousandsFormat =
      parts.length > 1 &&
      parts.every(part => /^\d+$/.test(part)) &&
      parts.slice(0, -1).every(part => part.length === 3) &&
      lastPart.length === 3;

    const isShortThousandsFormat =
      parts.length === 2 &&
      /^\d{1,3}$/.test(parts[0]) &&
      parts[1].length === 3 &&
      /^\d+$/.test(parts[1]);

    if (isThousandsFormat || isShortThousandsFormat) {
      const parsed = Number(parts.join(''));
      return Number.isFinite(parsed) ? parsed : null;
    }

    if (parts.length === 2 && parts[1].length <= 2) {
      const parsed = Number(`${parts[0]}.${parts[1]}`);
      return Number.isFinite(parsed) ? parsed : null;
    }
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

const TITLE_NUMBER_PATTERN =
  /\d{1,3}(?:\.\d{3})+(?:,\d+)?|\d+,\d+|\d+\.\d+|\d+/g;

/** Extrae todos los números reconocibles del título de una tarea */
export function extractNumbersFromTitle(title: string): number[] {
  if (!title?.trim()) return [];

  const matches = title.match(TITLE_NUMBER_PATTERN) || [];
  const values: number[] = [];

  for (const token of matches) {
    const parsed = parseLocalizedNumber(token);
    if (parsed !== null) values.push(parsed);
  }

  return values;
}

/** Valor numérico agregado de una tarea (título primero, luego number_value) */
export function getTaskAggregateNumericValue(task: TaskBoardTask): number | null {
  const fromTitle = extractNumbersFromTitle(task.title);
  if (fromTitle.length > 0) {
    return fromTitle.reduce((acc, value) => acc + value, 0);
  }

  if (task.number_value != null && !Number.isNaN(task.number_value)) {
    return task.number_value;
  }

  return null;
}

export function computeColumnNumericStats(tasks: TaskBoardTask[]): {
  sum: number;
  average: number;
  count: number;
} | null {
  const values = tasks
    .map(getTaskAggregateNumericValue)
    .filter((value): value is number => value !== null && !Number.isNaN(value));

  if (values.length === 0) return null;

  const sum = values.reduce((acc, value) => acc + value, 0);
  return { sum, average: sum / values.length, count: values.length };
}

export function getColumnColors(project: TaskProject | null): Record<string, string> {
  if (!project?.column_colors || typeof project.column_colors !== 'object') return {};
  return project.column_colors;
}

/** Estilo inline de fondo para una columna (undefined = usar default CSS) */
export function getColumnBgStyle(
  column: string,
  project: TaskProject | null,
  isDraggingOver = false
): { backgroundColor: string } | undefined {
  if (isDraggingOver) return undefined;
  const hex = getColumnColors(project)[column];
  if (!hex) return undefined;
  return { backgroundColor: hexToRgba(hex, 0.18) };
}

export function normalizeTaskStatus(status: string, columns: string[]): string {
  const lower = status.toLowerCase();
  const match = columns.find(c => c.toLowerCase() === lower);
  if (match) return match;
  if (lower.includes('hecho') || lower.includes('complet')) return columns[columns.length - 1] || 'Hecho';
  if (lower.includes('proceso') || lower.includes('progreso')) return columns.find(c => c.toLowerCase().includes('proceso')) || columns[1] || columns[0];
  if (lower.includes('revis')) return columns.find(c => c.toLowerCase().includes('revis')) || columns[2] || columns[0];
  if (lower.includes('bloq')) return columns.find(c => c.toLowerCase().includes('bloq')) || columns[3] || columns[0];
  return columns[0];
}

export function filterTasks(tasks: TaskBoardTask[], filters: ProjectFilters): TaskBoardTask[] {
  return tasks.filter(task => {
    if (task.is_archived) return false;
    if (filters.hideCompleted && isTaskDone(task.status, task.completed)) return false;

    if (filters.myTasks) {
      return isAssigneeForUser(
        task.assignee,
        filters.currentUserEmail,
        filters.currentUserName,
        filters.systemUsers
      );
    }

    if (filters.search) {
      const q = filters.search.toLowerCase();
      const inTitle = task.title.toLowerCase().includes(q);
      const inDesc = (task.description || '').toLowerCase().includes(q);
      const inTags = (task.tags || []).some(t => t.toLowerCase().includes(q));
      if (!inTitle && !inDesc && !inTags) return false;
    }

    if (filters.assignee && task.assignee !== filters.assignee) return false;
    if (filters.priority && task.priority !== filters.priority) return false;
    if (filters.tag && !(task.tags || []).includes(filters.tag)) return false;

    if (filters.dateFrom && task.due_date && task.due_date < filters.dateFrom) return false;
    if (filters.dateTo && task.due_date && task.due_date > filters.dateTo) return false;

    return true;
  });
}

export function groupTasksByColumn(
  tasks: TaskBoardTask[],
  columns: string[]
): Record<string, TaskBoardTask[]> {
  const groups: Record<string, TaskBoardTask[]> = {};
  columns.forEach(col => { groups[col] = []; });

  for (const task of tasks) {
    const col = normalizeTaskStatus(task.status, columns);
    if (!groups[col]) groups[col] = [];
    groups[col].push(task);
  }

  for (const col of Object.keys(groups)) {
    groups[col].sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
  }

  return groups;
}

export function getChecklistProgress(task: TaskBoardTask): { done: number; total: number } {
  const items = task.checklist || [];
  if (items.length === 0) return { done: 0, total: 0 };
  return { done: items.filter(i => i.done).length, total: items.length };
}

export function collectAssignees(tasks: TaskBoardTask[]): string[] {
  const set = new Set<string>();
  tasks.forEach(t => { if (t.assignee?.trim()) set.add(t.assignee.trim()); });
  return Array.from(set).sort();
}

export function collectTags(tasks: TaskBoardTask[]): string[] {
  const set = new Set<string>();
  tasks.forEach(t => (t.tags || []).forEach(tag => { if (tag.trim()) set.add(tag.trim()); }));
  return Array.from(set).sort();
}

export function appendActivity(
  task: TaskBoardTask,
  message: string,
  author?: string | null
): TaskBoardTask['activity'] {
  const prev = task.activity || [];
  return [
    ...prev,
    { type: 'update', message, author: author ?? null, created_at: new Date().toISOString() },
  ];
}

export function getInitials(name?: string | null): string {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] || '') + (parts[parts.length - 1]?.[0] || '')).toUpperCase();
}
